// Service Configuration Loader
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import * as yaml from "js-yaml";

import debugLogger from "../../utils/debugLogger";
import { ServiceConfig, ServiceType } from "../types";

export interface ServicesConfigFlat {
  services: ServiceConfig[];
}

export interface ServicesConfigTiered {
  services: {
    platform?: ServiceConfig[];
    domain?: ServiceConfig[];
  };
}

export type ServicesConfig = ServicesConfigFlat | ServicesConfigTiered;

/**
 * Substitute environment variables in config values
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function substituteEnvVars(value: any): any {
  if (typeof value === "string") {
    // Replace ${VAR_NAME} with import.meta.env.VAR_NAME
    return value.replace(/\${([^}]+)}/g, (match, varName) => {
      return import.meta.env[varName] || match;
    });
  }

  if (Array.isArray(value)) {
    return value.map(substituteEnvVars);
  }

  if (value && typeof value === "object") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: Record<string, any> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = substituteEnvVars(val);
    }
    return result;
  }

  return value;
}

/**
 * Load and parse services configuration from YAML file
 * In Vite, we fetch the file from the public directory or use import
 */
export async function loadServicesConfig(
  configPath?: string,
): Promise<ServiceConfig[]> {
  const defaultPath = "/config/services.config.yml";
  const filePath = configPath || defaultPath;

  try {
    // Fetch the YAML file
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch config file: ${response.statusText}`);
    }
    const fileContents = await response.text();
    const parsed = yaml.load(fileContents) as ServicesConfig;

    if (!parsed || !parsed.services) {
      throw new Error("Invalid services configuration: missing services");
    }

    // Flatten tiered config (platform + domain) into a single array
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let rawServices: any[];
    if (Array.isArray(parsed.services)) {
      rawServices = parsed.services;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tiered = parsed.services as { platform?: any[]; domain?: any[] };
      rawServices = [...(tiered.platform || []), ...(tiered.domain || [])];
    }

    // Substitute environment variables and validate
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const services = rawServices.map((service: any) => {
      const config: ServiceConfig = {
        name: service.name,
        type: service.type as ServiceType,
        class: service.class,
        factory: service.factory,
        module: service.module,
        dependencies: service.dependencies || [],
        config: substituteEnvVars(service.config || {}),
        enabled: service.enabled !== false,
        priority: service.priority || 100,
      };

      // Validate required fields
      if (!config.name) {
        throw new Error("Service config missing required field: name");
      }
      if (!config.type) {
        throw new Error(`Service ${config.name} missing required field: type`);
      }
      if (!config.module) {
        throw new Error(
          `Service ${config.name} missing required field: module`,
        );
      }
      if (config.type === ServiceType.FACTORY && !config.factory) {
        throw new Error(
          `Service ${config.name} is factory type but missing factory function name`,
        );
      }
      if (config.type !== ServiceType.FACTORY && !config.class) {
        throw new Error(`Service ${config.name} missing required field: class`);
      }

      return config;
    });

    return services;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(
        `Failed to load services config from ${filePath}: ${error.message}`,
      );
    }
    throw error;
  }
}

/**
 * Synchronous version for environments that support it (Node.js)
 * Falls back to empty array in browser
 */
export function loadServicesConfigSync(configPath?: string): ServiceConfig[] {
  // In browser environment, return empty array
  // Use async loadServicesConfig instead
  if (typeof window !== "undefined") {
    debugLogger.warn(
      "loadServicesConfigSync called in browser, use loadServicesConfig instead",
    );
    return [];
  }

  // Node.js environment (for testing/build tools)
  try {
    const fs = require("fs");
    const path = require("path");
    const defaultPath = path.resolve(
      process.cwd(),
      "public",
      "config",
      "services.config.yml",
    );
    const filePath = configPath || defaultPath;
    const fileContents = fs.readFileSync(filePath, "utf8");
    const parsed = yaml.load(fileContents) as ServicesConfig;

    if (!parsed || !parsed.services) {
      throw new Error("Invalid services configuration: missing services");
    }

    // Flatten tiered config (platform + domain) into a single array
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let rawServices: any[];
    if (Array.isArray(parsed.services)) {
      rawServices = parsed.services;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tiered = parsed.services as { platform?: any[]; domain?: any[] };
      rawServices = [...(tiered.platform || []), ...(tiered.domain || [])];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const services = rawServices.map((service: any) => {
      const config: ServiceConfig = {
        name: service.name,
        type: service.type as ServiceType,
        class: service.class,
        factory: service.factory,
        module: service.module,
        dependencies: service.dependencies || [],
        config: substituteEnvVars(service.config || {}),
        enabled: service.enabled !== false,
        priority: service.priority || 100,
      };

      if (!config.name || !config.type || !config.module) {
        throw new Error(`Invalid service config: ${config.name || "unknown"}`);
      }

      return config;
    });

    return services;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to load services config: ${error.message}`);
    }
    throw error;
  }
}
